import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Check, BadgeCheck, GraduationCap } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { SERVICE_LANES } from "@/lib/services";
import { getRefCookie } from "@/lib/refCookie";
import { api } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=1920&q=85";

export default function Register() {
    const { register, formatApiError } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const rawRef = (searchParams.get("ref") || "").trim().toLowerCase();
    // Backend referral codes are 8 lowercase hex chars (uuid4().hex[:8]).
    // Fall back to the bula_ref cookie set by App.js's RefCookieCapture so
    // a user who landed on /properties/123?ref=ab12cd days ago still gets
    // attributed when they finally hit Register.
    const urlRef = /^[a-f0-9]{8}$/.test(rawRef) ? rawRef : "";
    const [referralCode, setReferralCode] = useState(urlRef || getRefCookie() || "");
    const [refAgent, setRefAgent] = useState(null); // { name, verified, trained, agency_name }
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [fullName, setFullName] = useState("");
    const [phone, setPhone] = useState("+679");
    const [services, setServices] = useState([]);
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Look up referrer name once we have a code, so we can show "Referred by X"
    useEffect(() => {
        if (!referralCode) { setRefAgent(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/share/agent-by-ref/${referralCode}`);
                if (!cancelled) setRefAgent(data);
            } catch {
                if (!cancelled) setRefAgent(null);  // 404 silently — code stored but not surfaced
            }
        })();
        return () => { cancelled = true; };
    }, [referralCode]);

    const toggleService = (id) =>
        setServices((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (services.length === 0) {
            setError("Please select at least one lane — what brings you to Bula Real Estate Fiji?");
            return;
        }
        setLoading(true);
        try {
            // Server derives roles[] from services automatically; we still pass legacy ['buyer'] default.
            await register({
                email,
                password,
                full_name: fullName,
                phone,
                roles: ["buyer"],
                services,
                referral_code: referralCode || undefined,
            });
            toast.success("Welcome to Bula Real Estate Fiji! Verify your phone next.");
            navigate("/verify-phone");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[80vh] grid lg:grid-cols-2">
            <div className="relative hidden lg:block">
                <img src={HERO_BG} alt="Fiji beach" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-ocean-deep/55" />
                <div className="relative z-10 flex flex-col justify-end h-full p-12 text-white">
                    <h2 className="font-display text-4xl font-bold leading-tight mb-3">
                        Join Fiji's all-in-one real estate, Joint Venture & business marketplace.
                    </h2>
                    <p className="font-app text-white/85 max-w-md">
                        Verified members. Vetted deals. Platform-routed messaging. One account, every lane —
                        land, Joint Ventures, businesses, and capital.
                    </p>
                </div>
            </div>

            <div className="flex items-center justify-center p-6 sm:p-12">
                <Card className="w-full max-w-xl p-8">
                    <h1 className="font-display text-3xl font-bold mb-2">Create your account</h1>
                    <p className="text-sm text-muted-foreground font-app mb-6">
                        Free to join. Phone verified by SMS. Email subscribes you to deal alerts.
                    </p>

                    {referralCode && (
                        <div data-testid="referral-banner" className="mb-5 p-3 rounded-md bg-coral/10 border border-coral/30 text-sm font-app">
                            {refAgent ? (
                                <>
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <span className="font-semibold text-coral">Referred by {refAgent.name}</span>
                                        {refAgent.verified && (
                                            <span className="inline-flex items-center gap-0.5 text-fiji-green text-xs" title="Verified Agent">
                                                <BadgeCheck className="h-3 w-3" /> Verified
                                            </span>
                                        )}
                                        {refAgent.trained && (
                                            <span className="inline-flex items-center gap-0.5 text-coral text-xs" title="Certified Bula Agent">
                                                <GraduationCap className="h-3 w-3" /> Certified
                                            </span>
                                        )}
                                        {refAgent.agency_name && (
                                            <span className="text-muted-foreground text-xs">· {refAgent.agency_name}</span>
                                        )}
                                    </div>
                                    <span className="text-foreground/80 text-xs">
                                        They earn a kickback when your deals close. Code{" "}
                                        <code className="font-mono px-1 py-0.5 rounded bg-card">{referralCode}</code>
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="font-semibold text-coral">You were invited!</span>{" "}
                                    <span className="text-foreground/80">Your referrer earns when your deals close — code <code className="font-mono px-1 py-0.5 rounded bg-card">{referralCode}</code></span>
                                </>
                            )}
                        </div>
                    )}

                    <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="full_name">Full name</Label>
                                <Input id="full_name" data-testid="register-name-input" required value={fullName} onChange={(e) => setFullName(e.target.value)} className="mt-1.5" />
                            </div>
                            <div>
                                <Label htmlFor="phone">Phone</Label>
                                <Input id="phone" data-testid="register-phone-input" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+679..." className="mt-1.5" />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input id="email" data-testid="register-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                            <Label htmlFor="password">Password</Label>
                            <div className="relative mt-1.5">
                                <Input id="password" data-testid="register-password-input" type={show ? "text" : "password"} required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" />
                                <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" aria-label="Toggle password visibility">
                                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <Label className="mb-2 block">What brings you here? <span className="text-muted-foreground font-normal">(select all that apply)</span></Label>
                            <div className="space-y-2" data-testid="register-services-list">
                                {SERVICE_LANES.map((s) => {
                                    const selected = services.includes(s.id);
                                    const Icon = s.icon;
                                    return (
                                        <label
                                            key={s.id}
                                            data-testid={`service-${s.id}`}
                                            className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition ${
                                                selected
                                                    ? "border-ocean bg-ocean/5 dark:border-turquoise dark:bg-turquoise/5"
                                                    : "border-border hover:border-muted-foreground/40"
                                            }`}
                                        >
                                            <Checkbox checked={selected} onCheckedChange={() => toggleService(s.id)} className="mt-0.5" />
                                            <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${s.accent}`} />
                                            <div className="min-w-0">
                                                <div className="font-app font-medium text-sm">{s.label}</div>
                                                <div className="text-xs text-muted-foreground font-app">{s.desc}</div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {error && <div data-testid="register-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-3 font-app">{error}</div>}

                        <Button type="submit" data-testid="register-submit-btn" disabled={loading} className="w-full bg-ocean-green text-white hover:opacity-90 font-app h-11">
                            {loading ? "Creating account…" : <>Create account <Check className="ml-2 h-4 w-4" /></>}
                        </Button>
                    </form>

                    <p className="text-sm text-center text-muted-foreground mt-6 font-app">
                        Already a member? <Link to="/login" className="text-coral font-medium hover:underline">Sign in</Link>
                    </p>
                </Card>
            </div>
        </div>
    );
}
